import { DateFromUnixDays } from 'app/entities/DateFromUnixDays';
import { pipe } from 'fp-ts/lib/function';
import * as t from 'io-ts';
import { NumberFromString } from 'io-ts-types';
import * as Either from 'fp-ts/Either';
import { fromEntries } from 'fp-ts/lib/Record';
import { GraphRateSide } from 'app/features/util/graph/graph';
import { DateFromUnixHours } from 'app/entities/DateFromUnixHours';
import { isNotNullNorUndefined } from '@silo/utils';
import { ChecksumAddress } from '@silo/types';

const DailyRateTimeseries = t.array(
  t.type({
    day: DateFromUnixDays,
    rateAvg: NumberFromString,
  })
);
type DailyRateTimeseries = t.TypeOf<typeof DailyRateTimeseries>;

const HourlyRateTimeseries = t.array(
  t.type({
    hour: DateFromUnixHours,
    rateAvg: NumberFromString,
  })
);
type HourlyRateTimeseries = t.TypeOf<typeof HourlyRateTimeseries>;

/**
 * type as it comes from the Graph, without any transformations
 */
const RawDailyRateTimeseries = t.array(t.type({ day: t.number, rateAvg: t.string }));
const RawHourlyRateTimeseries = t.array(t.type({ hour: t.number, rateAvg: t.string }));

/**
 * Adds compensation for missing daily rates in the API response.
 * Missing rates can happen when there is no transaction / event on that day
 * which would trigger an update of the interest rate on the Graph side.
 * As a result, missing rates should inherit the interest rate of the previous day (recursively).
 */
const DailyRateTimeseriesFromGraph = new t.Type<DailyRateTimeseries, DailyRateTimeseries, unknown>(
  'DailyRateTimeseriesFromGraph',
  DailyRateTimeseries.is,
  (u, c) => {
    return pipe(
      RawDailyRateTimeseries.validate(u, c),
      Either.map((timeseries) => {
        const days = timeseries.map((rate) => rate.day);
        const byDay = fromEntries(timeseries.map((rate) => [String(rate.day), rate]));
        const earliestDay = Math.min(...days);
        const latestDay = Math.max(...days);
        return fillDailyRangeWithPreviousAvailableValue(earliestDay, latestDay, byDay);
      }),
      Either.chain((ts) => DailyRateTimeseries.validate(ts, c))
    );
  },
  (rateTimeseries) => rateTimeseries
);

/**
 * Adds compensation for missing hourly rates in the API response.
 * Missing rates can happen when there is no transaction / event on that hour
 * which would trigger an update of the interest rate on the Graph side.
 * As a result, missing rates should inherit the interest rate of the previous hour (recursively).
 */
const HourlyRateTimeseriesFromGraph = new t.Type<HourlyRateTimeseries, HourlyRateTimeseries, unknown>(
  'HourlyRateTimeseriesFromGraph',
  HourlyRateTimeseries.is,
  (u, c) => {
    return pipe(
      RawHourlyRateTimeseries.validate(u, c),
      Either.map((timeseries) => {
        const hours = timeseries.map((rate) => rate.hour);
        const byHour = fromEntries(timeseries.map((rate) => [String(rate.hour), rate]));
        const earliestHour = Math.min(...hours);
        const latestHour = Math.max(...hours);
        return fillHourlyRangeWithPreviousAvailableValue(earliestHour, latestHour, byHour);
      }),
      Either.chain((ts) => HourlyRateTimeseries.validate(ts, c))
    );
  },
  (rateTimeseries) => rateTimeseries
);

const DailyRate = t.type({
  interestRateDaily: DailyRateTimeseriesFromGraph,
});
type DailyRate = t.TypeOf<typeof DailyRate>;

/**
 * Ensures that none of the individual assets' interestRateDaily are empty.
 * Fills any empty interestRateDaily arrays with 0s.
 */
const NonEmptyDailyRates = new t.Type<DailyRate[], DailyRate[], unknown>(
  'NonEmptyDailyRates',
  t.array(DailyRate).is,
  (u, c) => {
    return pipe(
      t.array(t.type({ interestRateDaily: RawDailyRateTimeseries })).validate(u, c),
      Either.map((rates) => {
        return rates.map((rate, _, all) => {
          if (rate.interestRateDaily.length !== 0) return { interestRateDaily: rate.interestRateDaily };

          const days = all.map((rate) => rate.interestRateDaily.map((rate) => rate.day)).flat();
          const earliestDay = Math.min(...days);
          const latestDay = Math.min(...days);

          if (!earliestDay || !latestDay) return rate;

          return {
            interestRateDaily: Array.from({ length: latestDay - earliestDay + 1 }, (_, i) => ({
              day: latestDay - i,
              rateAvg: '0',
            })),
          };
        });
      }),
      Either.chain((rates) => t.array(DailyRate).validate(rates, c))
    );
  },
  (rates) => rates
);

const HourlyRate = t.type({
  interestRateHourly: HourlyRateTimeseriesFromGraph,
});
type HourlyRate = t.TypeOf<typeof HourlyRate>;

/**
 * Ensures that none of the individual assets' interestRateHourly are empty.
 * Fills any empty interestRateHourly arrays with 0s.
 */
const NonEmptyHourlyRates = new t.Type<HourlyRate[], HourlyRate[], unknown>(
  'NonEmptyHourlyRates',
  t.array(HourlyRate).is,
  (u, c) => {
    return pipe(
      t.array(t.type({ interestRateHourly: RawHourlyRateTimeseries })).validate(u, c),
      Either.map((rates) => {
        return rates.map((rate, _, all) => {
          if (rate.interestRateHourly.length !== 0) return { interestRateHourly: rate.interestRateHourly };

          const hours = all.map((rate) => rate.interestRateHourly.map((rate) => rate.hour)).flat();
          const earliestHour = Math.min(...hours);
          const latestHour = Math.min(...hours);

          if (!earliestHour || !latestHour) return rate;

          return {
            interestRateHourly: Array.from({ length: latestHour - earliestHour + 1 }, (_, i) => ({
              hour: latestHour - i,
              rateAvg: '0',
            })),
          };
        });
      }),
      Either.chain((rates) => t.array(HourlyRate).validate(rates, c))
    );
  },
  (rates) => rates
);

const RateInfo = t.type({
  side: GraphRateSide,
  token: t.type({
    id: ChecksumAddress,
  }),
});

const Rate = t.intersection([HourlyRate, DailyRate, RateInfo]);
type Rate = t.TypeOf<typeof Rate>;

/**
 * Merges the various individual codecs that make up the Rate type into a single codec.
 */
const NonEmptyRates = new t.Type<Rate[], Rate[], unknown>(
  'NonEmptyRates',
  t.array(Rate).is,
  (rates, c) => {
    const hourly = NonEmptyHourlyRates.decode(rates);
    const daily = NonEmptyDailyRates.decode(rates);
    const other = t.array(RateInfo).decode(rates);

    if (!Either.isRight(hourly)) return t.failure(rates, c, 'rates arent NonEmptyHourlyRates');
    if (!Either.isRight(daily)) return t.failure(rates, c, 'rates arent NonEmptyDailyRates');
    if (!Either.isRight(other)) return t.failure(rates, c, 'rates arent an array of RateInfo');

    const merged = hourly.right.map((hourlyRate, index) => {
      return {
        ...other.right[index],
        ...hourlyRate,
        ...daily.right[index],
      };
    });

    return t.success(merged);
  },
  (rates) => rates
);

export const GraphInterestRates = t.type({
  silo: t.type({
    id: ChecksumAddress,
    rates: NonEmptyRates,
  }),
});

export type GraphInterestRates = t.TypeOf<typeof GraphInterestRates>;

const fillRangeWithPreviousAvailableValue =
  <T>(forPreviousRate: (previous: T, timePeriod: number) => T) =>
  (earliest: number, latest: number, byTimePeriod: Record<string, T>) => {
    return Array.from({ length: latest - earliest + 1 }, (_, i) => latest - i)
      .map((timePeriod) => {
        const rate = byTimePeriod[String(timePeriod)];
        if (rate) return rate;

        const previousRate = getValueForPreviousAvailableTimePeriod(timePeriod, byTimePeriod);
        if (previousRate) {
          return forPreviousRate(previousRate, timePeriod);
        }
        return undefined;
      })
      .filter(isNotNullNorUndefined);
  };

const fillHourlyRangeWithPreviousAvailableValue = fillRangeWithPreviousAvailableValue<
  t.TypeOf<typeof RawHourlyRateTimeseries>[number]
>((previous, hour) => ({
  ...previous,
  hour,
}));

const fillDailyRangeWithPreviousAvailableValue = fillRangeWithPreviousAvailableValue<
  t.TypeOf<typeof RawDailyRateTimeseries>[number]
>((previous, day) => ({
  ...previous,
  day,
}));

const getValueForPreviousAvailableTimePeriod = <T>(timePeriod: number, byTimePeriod: Record<string, T>): T => {
  const valueForPreviousAvailableTimePeriod = byTimePeriod[String(timePeriod - 1)];
  if (valueForPreviousAvailableTimePeriod) return valueForPreviousAvailableTimePeriod;

  return getValueForPreviousAvailableTimePeriod(timePeriod - 1, byTimePeriod);
};
